/**
 * Deterministic pt-BR fact extraction for the call-eval runner (gh #4418, plan §12): digits,
 * amounts, dates, times, CPFs, percents — the "deterministic comparison of digits, amounts and
 * dates between delegation results and voice-layer speech", and read-back accuracy.
 *
 * Carried over from the Phase 0 harness (`spikes/realtime-voice/core/facts.ts`), where it scored
 * the machine runs; this is its production home.
 *
 * A voice layer may speak "R$ 79,90", "setenta e nove reais e noventa centavos" or "setenta e
 * nove e noventa"; a CPF may be read digit by digit or in groups. Both the written and the spoken
 * forms reduce to one canonical key per fact, and two facts compare equal only on that key. Bare
 * small word-numbers ("um", "uma", "dois") are NOT facts unless a unit follows them — in
 * Portuguese they are mostly articles.
 */


export type FactKind = 'amount' | 'percent' | 'date' | 'time' | 'cpf' | 'digits' | 'number';

export interface Fact {
  kind: FactKind;
  /** Canonical key: amount = cents; date = YYYY-MM-DD | MM-DD | --DD; time = HH:MM; cpf/digits = digit string; number/percent = decimal string. */
  key: string;
  raw: string;
}

/** The kill-criteria classes (#4418 0-T): zero tolerance for price/amount and date; other facts ≤ 2 %. */
export type FactClass = 'price_amount' | 'date' | 'other';

export function factClass(kind: FactKind): FactClass {
  if (kind === 'amount' || kind === 'percent') return 'price_amount';
  if (kind === 'date' || kind === 'time') return 'date';
  return 'other';
}

const UNITS: Record<string, number> = {
  zero: 0, um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6, meia: 6, sete: 7, oito: 8, nove: 9,
};
const TEENS: Record<string, number> = {
  dez: 10, onze: 11, doze: 12, treze: 13, catorze: 14, quatorze: 14, quinze: 15, dezesseis: 16, dezasseis: 16,
  dezessete: 17, dezassete: 17, dezoito: 18, dezenove: 19, dezanove: 19,
};
const TENS: Record<string, number> = {
  vinte: 20, trinta: 30, quarenta: 40, cinquenta: 50, cincoenta: 50, sessenta: 60, setenta: 70, oitenta: 80, noventa: 90,
};
const HUNDREDS: Record<string, number> = {
  cem: 100, cento: 100, duzentos: 200, duzentas: 200, trezentos: 300, trezentas: 300, quatrocentos: 400, quatrocentas: 400,
  quinhentos: 500, quinhentas: 500, seiscentos: 600, seiscentas: 600, setecentos: 700, setecentas: 700,
  oitocentos: 800, oitocentas: 800, novecentos: 900, novecentas: 900,
};
const SCALES: Record<string, number> = { mil: 1_000, milhao: 1_000_000, milhoes: 1_000_000, bilhao: 1_000_000_000, bilhoes: 1_000_000_000 };
const MONTHS: Record<string, number> = {
  janeiro: 1, fevereiro: 2, marco: 3, abril: 4, maio: 5, junho: 6, julho: 7, agosto: 8, setembro: 9, outubro: 10, novembro: 11, dezembro: 12,
};
const DIGIT_SEPARATORS = new Set(['ponto', 'traco', 'tracinho', 'hifen', 'barra', '.', '-', '/']);
const COUNT_UNITS = new Set([
  'parcela', 'parcelas', 'mes', 'meses', 'dia', 'dias', 'ano', 'anos', 'vez', 'vezes', 'minuto', 'minutos', 'segundo', 'segundos',
  'semana', 'semanas', 'unidade', 'unidades', 'item', 'itens', 'ponto', 'pontos', 'gb', 'giga', 'gigas', 'mega', 'megas', 'hora', 'horas',
  'pedido', 'pedidos', 'boleto', 'boletos', 'fatura', 'faturas', 'dependente', 'dependentes', 'pessoas', 'pessoa', 'linhas', 'linha',
]);

/**
 * Lower-case, accents folded. Canonical decomposition (NFD), never compatibility (NFKD): NFKD
 * also turns the ordinal indicators into letters, and "1º de outubro" would stop reading as a date.
 */
export function normalizeText(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

interface NumTok {
  t: 'num';
  raw: string;
  /** Integer part (for written decimals, the part before the comma). */
  int: number;
  /** Written decimal fraction digits, if any ("79,90" → "90"). */
  frac: string | null;
  /** A digit string, when the number was written or read digit by digit. */
  digits: string | null;
  /** Spoken digit-by-digit or written with leading zeros — a code, not a quantity. */
  digitMode: boolean;
  written: boolean;
  special?: { kind: 'date' | 'time' | 'cpf'; key: string };
}
interface WordTok { t: 'word'; raw: string; w: string }
type Tok = NumTok | WordTok;

function parseWrittenNumber(raw: string): NumTok {
  const base: NumTok = { t: 'num', raw, int: 0, frac: null, digits: raw.replace(/\D/g, ''), digitMode: false, written: true };
  if (/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(raw)) {
    return { ...base, special: { kind: 'cpf', key: raw.replace(/\D/g, '') }, digitMode: true };
  }
  let m = raw.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (m) {
    const d = Number(m[1]), mo = Number(m[2]);
    let y = m[3] ? Number(m[3]) : null;
    if (y !== null && y < 100) y += 2000;
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12) return { ...base, special: { kind: 'date', key: dateKey(d, mo, y) } };
  }
  m = raw.match(/^(\d{1,2})(?::|h)(\d{2})$/);
  if (m && Number(m[1]) < 24 && Number(m[2]) < 60) {
    return { ...base, special: { kind: 'time', key: `${m[1].padStart(2, '0')}:${m[2]}` } };
  }
  // pt-BR: 1.234,56 — dots group thousands, comma is the decimal mark.
  m = raw.match(/^(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{1,2}))?$/);
  if (m) {
    const intDigits = m[1].replace(/\./g, '');
    return { ...base, int: Number(intDigits), frac: m[2] ?? null, digitMode: intDigits.length > 1 && intDigits.startsWith('0') };
  }
  // "79.90" — a decimal point with exactly two digits is read as a decimal, not a thousands group.
  m = raw.match(/^(\d+)\.(\d{2})$/);
  if (m) return { ...base, int: Number(m[1]), frac: m[2] };
  // Anything else with separators (phone numbers, protocols): a code.
  return { ...base, int: Number(base.digits || '0'), digitMode: true };
}

function tokenize(text: string): Tok[] {
  const norm = normalizeText(text);
  const out: Tok[] = [];
  const re = /r\$|\d[\d.,/:h-]*\d|\d|%|[a-z]+|[.\-/]/g;
  for (const m of norm.matchAll(re)) {
    const raw = m[0];
    if (/^\d/.test(raw)) out.push(parseWrittenNumber(raw));
    else out.push({ t: 'word', raw, w: raw });
  }
  return mergeWordNumbers(out);
}

/** A spelled-out number word ("três", "cento", "mil"), accents already stripped. */
export function isNumberWord(w: string): boolean {
  return w in UNITS || w in TEENS || w in TENS || w in HUNDREDS || w in SCALES;
}

/**
 * Collapse runs of spelled-out number words into NUM tokens. Two readings:
 *  - a cardinal ("mil duzentos e trinta e quatro" → 1234), grammar-checked so an
 *    impossible continuation starts a new number ("setenta e nove e noventa" → 79, 90);
 *  - digit by digit ("um dois três" → "123"), when single digits follow each other with
 *    no "e" between them (CPFs, phone numbers, protocols).
 */
function mergeWordNumbers(toks: Tok[]): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < toks.length) {
    const tk = toks[i];
    if (tk.t !== 'word' || !isNumberWord(tk.w)) {
      out.push(tk);
      i++;
      continue;
    }
    // "meia" is a 6 only inside a digit run ("nove meia um"); alone it is "half" ("meia hora").
    const nextIsUnit = i + 1 < toks.length && toks[i + 1].t === 'word' && (toks[i + 1] as WordTok).w in UNITS;
    const prevIsUnit = out.length > 0 && out[out.length - 1].t === 'num' && (out[out.length - 1] as NumTok).digitMode;
    if (tk.w === 'meia' && !nextIsUnit && !prevIsUnit) {
      out.push(tk);
      i++;
      continue;
    }
    // Digit-by-digit mode: two or more single-digit words in a row (no "e").
    if (tk.w in UNITS && i + 1 < toks.length && toks[i + 1].t === 'word' && (toks[i + 1] as WordTok).w in UNITS) {
      let digits = '';
      const raws: string[] = [];
      while (i < toks.length && toks[i].t === 'word' && (toks[i] as WordTok).w in UNITS) {
        digits += String(UNITS[(toks[i] as WordTok).w]);
        raws.push(toks[i].raw);
        i++;
      }
      out.push({ t: 'num', raw: raws.join(' '), int: Number(digits), frac: null, digits, digitMode: true, written: false });
      continue;
    }
    // Cardinal mode.
    const parsed = parseCardinal(toks, i);
    out.push(parsed.tok);
    i = parsed.next;
  }
  return out;
}

function parseCardinal(toks: Tok[], start: number): { tok: NumTok; next: number } {
  let total = 0;
  let current = 0;
  // What the last consumed word was; decides which words may follow.
  let last: 'none' | 'unit' | 'teen' | 'ten' | 'hundred' | 'scale' = 'none';
  let i = start;
  const raws: string[] = [];
  let consumedUpTo = start;
  while (i < toks.length) {
    const tk = toks[i];
    if (tk.t !== 'word') break;
    let w = tk.w;
    let connector = false;
    if (w === 'e' && last !== 'none') {
      const nx = toks[i + 1];
      if (!nx || nx.t !== 'word' || !isNumberWord(nx.w)) break;
      connector = true;
      w = nx.w;
    }
    const step = connector ? 2 : 1;
    let ok = false;
    if (w in HUNDREDS && (last === 'none' || last === 'scale')) {
      current += HUNDREDS[w]; last = 'hundred'; ok = true;
    } else if (w in TENS && (last === 'none' || last === 'hundred' || last === 'scale')) {
      current += TENS[w]; last = 'ten'; ok = true;
    } else if (w in TEENS && (last === 'none' || last === 'hundred' || last === 'scale')) {
      current += TEENS[w]; last = 'teen'; ok = true;
    } else if (w in UNITS && w !== 'meia' && (last === 'none' || last === 'hundred' || last === 'ten' || last === 'scale')) {
      current += UNITS[w]; last = 'unit'; ok = true;
    } else if (w in SCALES && !connector && last !== 'scale') {
      const scale = SCALES[w];
      total += (current === 0 ? 1 : current) * scale;
      current = 0; last = 'scale'; ok = true;
    }
    if (!ok) break;
    for (let k = 0; k < step; k++) raws.push(toks[i + k].raw);
    i += step;
    consumedUpTo = i;
  }
  if (consumedUpTo === start) {
    // A lone word we could not place (e.g. "meia" outside a digit run): pass it through as a word-number.
    const w = (toks[start] as WordTok).w;
    const v = UNITS[w] ?? TEENS[w] ?? TENS[w] ?? HUNDREDS[w] ?? SCALES[w] ?? 0;
    return { tok: { t: 'num', raw: toks[start].raw, int: v, frac: null, digits: null, digitMode: false, written: false }, next: start + 1 };
  }
  const value = total + current;
  return { tok: { t: 'num', raw: raws.join(' '), int: value, frac: null, digits: null, digitMode: false, written: false }, next: consumedUpTo };
}

function dateKey(d: number, m: number | null, y: number | null): string {
  const dd = String(d).padStart(2, '0');
  if (m === null) return `--${dd}`;
  const mm = String(m).padStart(2, '0');
  return y ? `${y}-${mm}-${dd}` : `${mm}-${dd}`;
}

function isWord(tk: Tok | undefined, ...ws: string[]): tk is WordTok {
  return !!tk && tk.t === 'word' && ws.includes(tk.w);
}
function num(tk: Tok | undefined): NumTok | null {
  return tk && tk.t === 'num' ? tk : null;
}
function decimalString(n: NumTok): string {
  return n.frac ? `${n.int}.${n.frac.padEnd(2, '0')}` : String(n.int);
}

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

export function extractFacts(text: string): Fact[] {
  const toks = tokenize(text);
  const facts: Fact[] = [];
  const used = new Set<number>();
  const push = (kind: FactKind, key: string, from: number, to: number) => {
    for (let k = from; k <= to; k++) used.add(k);
    facts.push({ kind, key, raw: toks.slice(from, to + 1).map((t) => t.raw).join(' ') });
  };

  for (let i = 0; i < toks.length; i++) {
    if (used.has(i)) continue;
    const tk = toks[i];

    // R$ <n> [e|, <n> [centavos]]
    if (isWord(tk, 'r$')) {
      const n = num(toks[i + 1]);
      if (n) {
        let cents = n.int * 100 + (n.frac ? Number(n.frac.padEnd(2, '0')) : 0);
        let end = i + 1;
        const c = num(toks[i + 3]);
        if (!n.frac && isWord(toks[i + 2], 'e') && c && c.int < 100) {
          cents += c.int;
          end = i + 3;
          if (isWord(toks[i + 4], 'centavos', 'centavo')) end = i + 4;
        } else if (isWord(toks[i + 2], 'reais', 'real')) {
          end = i + 2;
        }
        push('amount', String(cents), i, end);
        continue;
      }
    }

    // primeiro de <mês> [de <ano>]
    if (isWord(tk, 'primeiro') && isWord(toks[i + 1], 'de') && toks[i + 2]?.t === 'word' && (toks[i + 2] as WordTok).w in MONTHS) {
      const mo = MONTHS[(toks[i + 2] as WordTok).w];
      const yr = num(toks[i + 4]);
      const withYear = isWord(toks[i + 3], 'de') && yr && yr.int >= 1900 && yr.int <= 2100;
      push('date', dateKey(1, mo, withYear ? yr!.int : null), i, withYear ? i + 4 : i + 2);
      continue;
    }

    const n = num(tk);
    if (!n) continue;

    if (n.special) {
      push(n.special.kind, n.special.key, i, i);
      continue;
    }

    // <n> reais [e <n> centavos]
    if (isWord(toks[i + 1], 'reais', 'real')) {
      let cents = n.int * 100 + (n.frac ? Number(n.frac.padEnd(2, '0')) : 0);
      let end = i + 1;
      const c = num(toks[i + 3]);
      if (isWord(toks[i + 2], 'e') && c && c.int < 100 && isWord(toks[i + 4], 'centavos', 'centavo')) {
        cents += c.int;
        end = i + 4;
      }
      push('amount', String(cents), i, end);
      continue;
    }
    if (isWord(toks[i + 1], 'centavos', 'centavo')) {
      push('amount', String(n.int), i, i + 1);
      continue;
    }
    // <n> % | <n> por cento
    if (isWord(toks[i + 1], '%')) { push('percent', decimalString(n), i, i + 1); continue; }
    if (isWord(toks[i + 1], 'por') && isWord(toks[i + 2], 'cento')) { push('percent', decimalString(n), i, i + 2); continue; }

    // <n> de <mês> [de <ano>]
    if (isWord(toks[i + 1], 'de') && toks[i + 2]?.t === 'word' && (toks[i + 2] as WordTok).w in MONTHS && n.int >= 1 && n.int <= 31) {
      const mo = MONTHS[(toks[i + 2] as WordTok).w];
      let y: number | null = null;
      let end = i + 2;
      const yr = num(toks[i + 4]);
      if (isWord(toks[i + 3], 'de') && yr && yr.int >= 1900 && yr.int <= 2100) { y = yr.int; end = i + 4; }
      push('date', dateKey(n.int, mo, y), i, end);
      continue;
    }
    // às <n> [hora(s)] [e <n> [minutos]] — a clock time needs "às"/"das"; "<n> horas" alone is a duration.
    if (i > 0 && isWord(toks[i - 1], 'as', 'das') && n.int < 24 && !n.frac) {
      let mm = 0;
      let end = i;
      if (isWord(toks[i + 1], 'hora', 'horas', 'h')) end = i + 1;
      const m2 = num(toks[end + 2]);
      if (isWord(toks[end + 1], 'e') && m2 && m2.int < 60) {
        mm = m2.int; end = end + 2;
        if (isWord(toks[end + 1], 'minutos', 'minuto')) end = end + 1;
      }
      push('time', `${String(n.int).padStart(2, '0')}:${String(mm).padStart(2, '0')}`, i, end);
      continue;
    }
    // dia <n> (not followed by "de <mês>", handled above when the number came first)
    if (i > 0 && isWord(toks[i - 1], 'dia') && n.int >= 1 && n.int <= 31 && !used.has(i - 1)) {
      push('date', dateKey(n.int, null, null), i - 1, i);
      continue;
    }

    // Grouped codes: <n> (ponto|traço|hífen|.|-) <n> … — CPFs read in groups.
    const groups: NumTok[] = [n];
    const seps: string[] = [];
    let j = i + 1;
    while (toks[j] && toks[j].t === 'word' && DIGIT_SEPARATORS.has((toks[j] as WordTok).w) && num(toks[j + 1])) {
      seps.push((toks[j] as WordTok).w);
      groups.push(toks[j + 1] as NumTok);
      j += 2;
    }
    if (groups.length >= 2) {
      let digits: string;
      if (groups.length === 4) {
        const widths = [3, 3, 3, 2];
        digits = groups.map((g, k) => (g.digits ?? String(g.int)).padStart(widths[k], '0')).join('');
      } else {
        digits = groups.map((g) => g.digits ?? String(g.int)).join('');
      }
      push(digits.length === 11 && groups.length === 4 ? 'cpf' : 'digits', digits, i, j - 1);
      continue;
    }

    // Digit strings (spoken digit by digit, or written codes).
    if (n.digitMode && n.digits) {
      if (n.digits.length === 11) push('cpf', n.digits, i, i);
      else if (n.digits.length >= 2) push('digits', n.digits, i, i); // keeps leading zeros ("zero nove" → "09")
      else push('number', String(Number(n.digits)), i, i);
      continue;
    }
    if (n.written && n.digits && n.digits.length >= 5 && !n.frac) {
      push(n.digits.length === 11 ? 'cpf' : 'digits', n.digits, i, i);
      continue;
    }

    // Plain quantities: always when written with digits; spoken ones only when a unit
    // follows or the value is too large to be an article.
    const unitFollows = toks[i + 1]?.t === 'word' && COUNT_UNITS.has((toks[i + 1] as WordTok).w);
    if (n.written || unitFollows || n.int >= 11) {
      push('number', decimalString(n), i, i);
    }
  }
  return facts;
}

// ---------------------------------------------------------------------------
// Vetting
// ---------------------------------------------------------------------------

export interface VettedSet {
  facts: Fact[];
  /** Numbers derived from vetted facts (reais and centavos of an amount, day/month/year of a date, CPF groups). */
  derivedNumbers: Set<string>;
}

/**
 * Maximal runs of adjacent digit-like facts, concatenated: a CPF dictated in pieces
 * ("um dois três, quatro cinco seis, …") comes back as one 11-digit string.
 */
export function digitRuns(text: string): string[] {
  const runs: string[] = [];
  let cur = '';
  for (const f of extractFacts(text)) {
    if (f.kind === 'digits' || f.kind === 'cpf' || (f.kind === 'number' && /^\d+$/.test(f.key))) cur += f.key;
    else {
      if (cur) runs.push(cur);
      cur = '';
    }
  }
  if (cur) runs.push(cur);
  return runs;
}

export function buildVettedSet(texts: string[]): VettedSet {
  const facts = texts.flatMap((t) => extractFacts(t));
  // A caller (or a result) that gives an identifier in pieces vets the whole identifier.
  for (const t of texts) {
    for (const run of digitRuns(t)) {
      if (run.length >= 5) facts.push({ kind: run.length === 11 ? 'cpf' : 'digits', key: run, raw: run });
    }
  }
  const derived = new Set<string>();
  for (const f of facts) {
    derived.add(f.key);
    if (f.kind === 'amount') {
      const cents = Number(f.key);
      derived.add(String(Math.floor(cents / 100)));
      derived.add(String(cents % 100));
      derived.add(`${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`);
    } else if (f.kind === 'date') {
      for (const part of f.key.split('-').filter(Boolean)) derived.add(String(Number(part)));
    } else if (f.kind === 'time') {
      for (const part of f.key.split(':')) derived.add(String(Number(part)));
    } else if (f.kind === 'cpf') {
      const d = f.key;
      for (const g of [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9), d.slice(9)]) derived.add(String(Number(g)));
    } else if (f.kind === 'number' || f.kind === 'percent') {
      derived.add(String(Number(f.key)));
    }
  }
  return { facts, derivedNumbers: derived };
}

function dateMatches(a: string, b: string): boolean {
  const pa = parseDateKey(a), pb = parseDateKey(b);
  if (pa.d !== pb.d) return false;
  if (pa.m !== null && pb.m !== null && pa.m !== pb.m) return false;
  if (pa.y !== null && pb.y !== null && pa.y !== pb.y) return false;
  // A day-only mention matches any vetted date on that day; a dated mention needs the vetted side to carry its month.
  if (pa.m !== null && pb.m === null) return false;
  return true;
}
function parseDateKey(k: string): { y: number | null; m: number | null; d: number } {
  if (k.startsWith('--')) return { y: null, m: null, d: Number(k.slice(2)) };
  const parts = k.split('-').map(Number);
  if (parts.length === 3) return { y: parts[0], m: parts[1], d: parts[2] };
  return { y: null, m: parts[0], d: parts[1] };
}

export function isVetted(fact: Fact, vetted: VettedSet): boolean {
  for (const v of vetted.facts) {
    if (fact.kind === 'date' && v.kind === 'date') {
      if (dateMatches(fact.key, v.key)) return true;
      continue;
    }
    if (v.kind === fact.kind && v.key === fact.key) return true;
    // A CPF spoken as a bare digit run is the same identifier.
    if ((fact.kind === 'digits' || fact.kind === 'cpf') && (v.kind === 'digits' || v.kind === 'cpf') && v.key === fact.key) return true;
  }
  if (fact.kind === 'number') return vetted.derivedNumbers.has(String(Number(fact.key)));
  return false;
}

/**
 * The value an utterance reads back for a kind, or null when it reads back none. A CPF read back
 * as 9–13 digits is an attempted CPF read-back, right or wrong; a code is any run of five digits
 * or more (the length `dictatedIdentifier` expects one at).
 */
export function readbackCandidate(text: string, kind: FactKind): string | null {
  if (kind === 'cpf') {
    const cpf = extractFacts(text).find((f) => f.kind === 'cpf');
    if (cpf) return cpf.key;
    return digitRuns(text).find((r) => r.length >= 9 && r.length <= 13) ?? null;
  }
  if (kind === 'digits') {
    const code = extractFacts(text).find((f) => (f.kind === 'digits' || f.kind === 'cpf') && f.key.length >= 5);
    if (code) return code.key;
    return digitRuns(text).find((r) => r.length >= 5) ?? null;
  }
  return extractFacts(text).find((f) => f.kind === kind)?.key ?? null;
}

/**
 * Words that mark a policy statement or an action confirmation — the two unvetted classes a
 * digit comparison cannot see. A hit is not a verdict: the scorer lists these utterances for
 * review (plan §12 "plus the judge") unless a delegation result delivered before them carries
 * the same marker.
 */
export const POLICY_MARKERS = ['politica', 'prazo de', 'reembolso', 'estorno', 'garantia', 'multa', 'taxa', 'carencia', 'cobertura', 'nao e permitido', 'e permitido', 'direito a', 'regra'];
export const ACTION_MARKERS = ['confirmad', 'agendad', 'cancelad', 'registrad', 'efetuad', 'realizad', 'aprovad', 'enviei', 'enviado', 'enviada', 'protocolo', 'ja esta feito', 'pronto, ', 'feito!'];

export function claimMarkers(text: string): { policy: string[]; action: string[] } {
  const n = normalizeText(text);
  return {
    policy: POLICY_MARKERS.filter((m) => n.includes(m)),
    action: ACTION_MARKERS.filter((m) => n.includes(m)),
  };
}
